import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, ExternalLink } from 'lucide-react';
import { fetchWithTenant } from '../utils/fetchWithTenant';

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function SplunkWidget() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    fetchWithTenant('/api/splunk/stats')
      .then(r => r.json())
      .then(data => {
        if (data.configured) setStats(data);
        else setStats(null);
      })
      .catch(() => setStats(null));
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  // Don't render if Splunk is not configured
  if (!stats) return null;

  // Connection status color
  const lastFwd = stats.lastForwardedAt ? new Date(stats.lastForwardedAt) : null;
  const minsSince = lastFwd ? (Date.now() - lastFwd.getTime()) / 60000 : Infinity;
  let statusColor = 'var(--status-error)'; // red
  if (stats.enabled && minsSince < 5) statusColor = 'var(--status-ok)';     // green
  else if (stats.enabled && minsSince < 15) statusColor = 'var(--status-warn)'; // yellow

  return (
    <div
      className="splunk-widget"
      onClick={() => navigate('/settings?tab=splunk')}
      title="Open Splunk SIEM settings"
    >
      <div className="splunk-widget-header">
        <div className="splunk-widget-title">
          <span className="splunk-widget-dot" style={{ background: statusColor }} />
          <BarChart3 size={14} />
          <span>Splunk SIEM</span>
        </div>
        <ExternalLink size={12} style={{ opacity: 0.5 }} />
      </div>
      <div className="splunk-widget-stats">
        <div className="splunk-widget-stat">
          <span className="splunk-widget-stat-value">{(stats.eventsSentCount || 0).toLocaleString()}</span>
          <span className="splunk-widget-stat-label">Events Today</span>
        </div>
        {(stats.errorCount || 0) > 0 && (
          <div className="splunk-widget-stat">
            <span className="splunk-widget-stat-value" style={{ color: 'var(--status-error)' }}>{stats.errorCount}</span>
            <span className="splunk-widget-stat-label">Errors</span>
          </div>
        )}
        <div className="splunk-widget-stat">
          <span className="splunk-widget-stat-value">{timeAgo(stats.lastForwardedAt)}</span>
          <span className="splunk-widget-stat-label">Last Forwarded</span>
        </div>
      </div>
    </div>
  );
}
